// Helper waktu WIB (Asia/Jakarta, UTC+7). Data tetap disimpan UTC di backend;
// ini hanya untuk tampilan.

const HARI = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const BULAN_PENDEK = [
    "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
    "Jul", "Agu", "Sep", "Okt", "Nov", "Des",
];
const WD_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// Pecah sebuah waktu menjadi komponen jam-dinding WIB, apa pun zona waktu browser.
function wibParts(input) {
    const d = input ? new Date(input) : new Date();
    if (Number.isNaN(d.getTime())) return null;
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jakarta",
        weekday: "short",
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    let hour = get("hour");
    if (hour === "24") hour = "00"; // sebagian engine memakai '24' untuk tengah malam
    return {
        weekday: WD_INDEX[get("weekday")] ?? 0,
        day: get("day"),
        month: Number(get("month")),
        year: get("year"),
        hour,
        minute: get("minute"),
        second: get("second"),
    };
}

// "Minggu, 06 September 2026 19:16:03 WIB" — untuk jam digital live.
export function formatFullWIB(input) {
    const w = wibParts(input);
    if (!w) return "";
    return `${HARI[w.weekday]}, ${w.day} ${BULAN[w.month - 1]} ${w.year} `
        + `${w.hour}:${w.minute}:${w.second} WIB`;
}

// "Jumat, 9 Oktober 2026 21.49.30 WIB" — jam live di halaman login/verifikasi
// (tanggal tanpa nol di depan, jam.menit.detik dipisah titik).
export function formatFullWIBTitik(input) {
    const w = wibParts(input);
    if (!w) return "";
    return `${HARI[w.weekday]}, ${Number(w.day)} ${BULAN[w.month - 1]} ${w.year} `
        + `${w.hour}.${w.minute}.${w.second} WIB`;
}

// "06/09/2026 09:54:38 WIB" — untuk kolom timestamp di tabel/log. "" bila kosong.
export function formatWIB(input) {
    if (!input) return "";
    const w = wibParts(input);
    if (!w) return String(input);
    return `${w.day}/${String(w.month).padStart(2, "0")}/${w.year} ${w.hour}:${w.minute}:${w.second} WIB`;
}

// "09:54 WIB" — jam-menit saja.
export function formatWIBTime(input) {
    if (!input) return "";
    const w = wibParts(input);
    if (!w) return String(input);
    return `${w.hour}:${w.minute} WIB`;
}

// Apakah waktu ini masih di masa depan (mis. locked_until masih aktif).
export function isFuture(input) {
    if (!input) return false;
    const d = new Date(input);
    return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
}

// Dipakai Mode Gelap Otomatis: true kalau jam WIB saat ini masuk rentang malam
// (18:00-05:59). Sama persis dgn logika di script anti-flash public/index.html
// (yg tidak bisa import modul ini) -- jika range jam berubah, ubah di dua tempat.
export function isWibNight(input) {
    const w = wibParts(input);
    if (!w) return false;
    const hour = Number(w.hour);
    return hour >= 18 || hour < 6;
}

// "14 Sep 2026" -- label tanggal ringkas (dipakai mis. rentang periode rotasi klinik).
// Input tanggal-saja ("YYYY-MM-DD") aman diproses lewat wibParts (WIB selalu +7 dari
// UTC tengah malam, jadi tanggal kalender tidak pernah mundur).
export function formatTanggalPendek(input) {
    if (!input) return "";
    const w = wibParts(input);
    if (!w) return String(input);
    return `${Number(w.day)} ${BULAN_PENDEK[w.month - 1]} ${w.year}`;
}

// "14–18 Sep 2026" (rentang sebulan), "30 Sep – 2 Okt 2026" (beda bulan/tahun), atau satu tanggal
// "2 Jul 2026" bila mulai = akhir -- dipakai utk rentang Tanggal Mulai/Akhir Stase & Jaga Rotasi Klinik.
export function formatRentangTanggal(mulai, akhir) {
    if (!mulai && !akhir) return "";
    if (!mulai) return formatTanggalPendek(akhir);
    if (!akhir) return formatTanggalPendek(mulai);
    const a = wibParts(mulai);
    const b = wibParts(akhir);
    if (!a || !b) return `${formatTanggalPendek(mulai)} – ${formatTanggalPendek(akhir)}`;
    if (a.year === b.year && a.month === b.month && Number(a.day) === Number(b.day)) return formatTanggalPendek(mulai);
    if (a.year === b.year && a.month === b.month) {
        return `${Number(a.day)}–${Number(b.day)} ${BULAN_PENDEK[a.month - 1]} ${a.year}`;
    }
    return `${formatTanggalPendek(mulai)} – ${formatTanggalPendek(akhir)}`;
}

// Apakah HARI INI (WIB) berada di dalam rentang [mulai, akhir] inklusif -- dipakai
// badge "Sedang Berjalan" di monitoring Rotasi Klinik. Perbandingan string ISO
// (YYYY-MM-DD) valid krn urutan leksikografis = urutan kronologis utk format ini.
export function isDateRangeOngoing(mulai, akhir) {
    if (!mulai || !akhir) return false;
    const w = wibParts();
    if (!w) return false;
    const today = `${w.year}-${String(w.month).padStart(2, "0")}-${w.day}`;
    return mulai <= today && today <= akhir;
}
